import { useState, useEffect, useCallback, useRef } from 'react';
import { useUserProfile } from './useUserProfile';
import supabase from '../supabase/client';
import { WhatsAppInstance } from '../services/UazApiService';

interface DisconnectedInstance {
  id: string;
  name: string;
  clinic_id: number;
  disconnectedAt: Date;
  lastStatus: string;
}

interface UseInstanceMonitorReturn {
  disconnectedInstances: DisconnectedInstance[];
  isMonitoring: boolean;
  dismissAlert: (instanceId: string) => void;
  reconnectInstance: (instanceId: string) => Promise<void>;
  refreshInstances: () => Promise<void>;
}

/**
 * Hook para monitorar o status das instâncias do WhatsApp
 * Detecta quando instâncias ficam desconectadas e notifica o usuário
 */
export const useInstanceMonitor = (): UseInstanceMonitorReturn => {
  const { userProfile } = useUserProfile();
  const [disconnectedInstances, setDisconnectedInstances] = useState<DisconnectedInstance[]>([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousStatusRef = useRef<Map<string, string>>(new Map());

  /**
   * Busca todas as instâncias da clínica do usuário
   */
  const fetchInstances = useCallback(async (): Promise<WhatsAppInstance[]> => {
    if (!userProfile?.clinic_id) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('instances')
        .select('*')
        .eq('clinic_id', userProfile.clinic_id);

      if (error) {

        return [];
      }

      return data || [];
    } catch (error) {
      
      return [];
    }
  }, [userProfile?.clinic_id]);

  /**
   * Verifica o status das instâncias e detecta desconexões
   */
  const checkInstancesStatus = useCallback(async () => {
    const instances = await fetchInstances();
    const newDisconnectedInstances: DisconnectedInstance[] = [];
    const currentTime = new Date();

    instances.forEach((instance) => {
      const previousStatus = previousStatusRef.current.get(instance.id);
      const currentStatus = instance.status;

      // Atualiza o status anterior
      previousStatusRef.current.set(instance.id, currentStatus);

      // Verifica se a instância ficou desconectada
      if (
        currentStatus === 'disconnected' &&
        previousStatus &&
        previousStatus !== 'disconnected' &&
        !dismissedAlerts.has(instance.id)
      ) {
        newDisconnectedInstances.push({
          id: instance.id,
          name: instance.name,
          clinic_id: userProfile?.clinic_id || 0,
          disconnectedAt: currentTime,
          lastStatus: previousStatus
        });
      }
    });

    // Atualiza a lista de instâncias desconectadas
    setDisconnectedInstances(prev => {
      const existingIds = new Set(prev.map(inst => inst.id));
      const filteredNew = newDisconnectedInstances.filter(inst => !existingIds.has(inst.id));
      
      // Remove instâncias que não estão mais desconectadas
      const currentDisconnectedIds = new Set(
        instances
          .filter(inst => inst.status === 'disconnected')
          .map(inst => inst.id)
      );
      
      const stillDisconnected = prev.filter(inst => 
        currentDisconnectedIds.has(inst.id) && !dismissedAlerts.has(inst.id)
      );
      
      return [...stillDisconnected, ...filteredNew];
    });
  }, [fetchInstances, userProfile?.clinic_id, dismissedAlerts]);

  /**
   * Dispensa um alerta de instância desconectada
   */
  const dismissAlert = useCallback((instanceId: string) => {
    setDismissedAlerts(prev => new Set([...prev, instanceId]));
    setDisconnectedInstances(prev => prev.filter(inst => inst.id !== instanceId));
  }, []);

  /**
   * Tenta reconectar uma instância
   */
  const reconnectInstance = useCallback(async (instanceId: string) => {
    try {
      // Aqui você pode implementar a lógica de reconexão
      // Por exemplo, chamar a API para reconectar a instância
      const { error } = await supabase
        .from('instances')
        .update({ 
          status: 'connecting',
          updated_at: new Date().toISOString()
        })
        .eq('id', instanceId);

      if (error) {
        throw error;
      }

      // Remove da lista de desconectadas após tentar reconectar
      dismissAlert(instanceId);
      
      // Força uma verificação após 5 segundos
      setTimeout(() => {
        checkInstancesStatus();
      }, 5000);
    } catch (error) {
      
      throw error;
    }
  }, [dismissAlert, checkInstancesStatus]);

  /**
   * Força uma atualização das instâncias
   */
  const refreshInstances = useCallback(async () => {
    await checkInstancesStatus();
  }, [checkInstancesStatus]);

  /**
   * Inicia o monitoramento das instâncias
   */
  const startMonitoring = useCallback(() => {
    if (!userProfile?.clinic_id || intervalRef.current) {
      return;
    }

    setIsMonitoring(true);
    
    // Verificação inicial
    checkInstancesStatus();
    
    // Configura verificação periódica a cada 60 segundos
    intervalRef.current = setInterval(() => {
      checkInstancesStatus();
    }, 60000);
  }, [userProfile?.clinic_id, checkInstancesStatus]);

  /**
   * Para o monitoramento das instâncias
   */
  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  // Efeito para iniciar/parar monitoramento baseado no usuário logado
  useEffect(() => {
    if (userProfile?.clinic_id) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    // Cleanup ao desmontar o componente
    return () => {
      stopMonitoring();
    };
  }, [userProfile?.clinic_id, startMonitoring, stopMonitoring]);

  // Limpa alertas dispensados quando o usuário muda
  useEffect(() => {
    setDismissedAlerts(new Set());
    setDisconnectedInstances([]);
    previousStatusRef.current.clear();
  }, [userProfile?.clinic_id]);

  return {
    disconnectedInstances,
    isMonitoring,
    dismissAlert,
    reconnectInstance,
    refreshInstances
  };
};

export default useInstanceMonitor;